(() => {
  const config = window.DiggnLocalCurrency;
  if (!config || config.country === 'SA') return;

  const CACHE_KEY = 'diggnit-display-rates-sar-v1';
  const PREFERENCE_KEY = `diggnit-display-currency-v2-${config.country}`;
  const FRESH_CACHE_MS = 24 * 60 * 60 * 1000;
  const MAX_CACHE_MS = 72 * 60 * 60 * 1000;
  const MAX_PROVIDER_AGE_MS = 8 * 24 * 60 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 4500;

  const supportedCurrencies = new Set(config.currencies);
  const gccCurrencies = new Set(['AED', 'KWD', 'QAR', 'BHD', 'OMR']);
  const gccArabicCurrencyLabels = {
    AED: 'د.إ',
    KWD: 'د.ك',
    QAR: 'ر.ق',
    BHD: 'د.ب',
    OMR: 'ر.ع',
  };
  const state = {
    currency: resolveInitialCurrency(),
    rates: null,
    providerDate: null,
  };

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Currency display must never interfere with shopping.
    }
  }

  function resolveInitialCurrency() {
    const savedCurrency = safeStorageGet(PREFERENCE_KEY);
    if (savedCurrency && supportedCurrencies.has(savedCurrency)) return savedCurrency;

    const countryCurrency = config.countryCurrencyMap[config.country];
    if (countryCurrency && supportedCurrencies.has(countryCurrency)) return countryCurrency;

    return config.fallbackCurrency;
  }

  function readCachedRates() {
    const raw = safeStorageGet(CACHE_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      const age = Date.now() - Number(parsed.fetchedAt);
      if (!Number.isFinite(age) || age < 0 || age > MAX_CACHE_MS) return null;
      if (!parsed.rates || typeof parsed.rates !== 'object') return null;

      const rates = {};
      config.currencies.forEach((currency) => {
        const rate = Number(parsed.rates[currency]);
        if (Number.isFinite(rate) && rate > 0) rates[currency] = rate;
      });

      if (!rates[state.currency]) return null;
      return {
        age,
        fetchedAt: Number(parsed.fetchedAt),
        providerDate: parsed.providerDate || null,
        rates,
      };
    } catch {
      return null;
    }
  }

  function validateProviderDate(rows) {
    const timestamps = rows
      .map((row) => Date.parse(`${row.date}T00:00:00Z`))
      .filter((value) => Number.isFinite(value));
    if (!timestamps.length) return null;

    const newest = Math.max(...timestamps);
    const age = Date.now() - newest;
    if (age < 0 || age > MAX_PROVIDER_AGE_MS) return null;
    return new Date(newest).toISOString().slice(0, 10);
  }

  async function requestRates() {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const query = new URLSearchParams({
      base: 'SAR',
      quotes: config.currencies.join(','),
    });

    try {
      const response = await fetch(`${config.rateEndpoint}?${query}`, {
        credentials: 'omit',
        mode: 'cors',
        referrerPolicy: 'no-referrer',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Rate request failed with ${response.status}`);

      const rows = await response.json();
      if (!Array.isArray(rows)) throw new Error('Rate response was not a list');

      const providerDate = validateProviderDate(rows);
      if (!providerDate) throw new Error('Rate response was stale or undated');

      const rates = {};
      rows.forEach((row) => {
        const currency = String(row.quote || '').toUpperCase();
        const rate = Number(row.rate);
        if (row.base === 'SAR' && supportedCurrencies.has(currency) && Number.isFinite(rate) && rate > 0) {
          rates[currency] = rate;
        }
      });

      if (!rates[state.currency]) throw new Error('Selected display currency was unavailable');

      const cachedValue = {
        fetchedAt: Date.now(),
        providerDate,
        rates,
      };
      safeStorageSet(CACHE_KEY, JSON.stringify(cachedValue));
      return cachedValue;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function resolveDisplayAmount(sarCents, currency) {
    const rate = state.rates?.[currency];
    if (!Number.isFinite(rate) || rate <= 0) return null;

    const convertedAmount = (sarCents / 100) * rate;
    const roundingRule = config.roundingRules?.[currency];
    if (!roundingRule) {
      return { amount: convertedAmount, fractionDigits: null };
    }

    const increment = Number(roundingRule.increment);
    const fractionDigits = Number(roundingRule.fractionDigits);
    if (!Number.isFinite(increment) || increment <= 0 || !Number.isInteger(fractionDigits)) {
      return { amount: convertedAmount, fractionDigits: null };
    }

    const roundedAmount = Math.ceil((convertedAmount - Number.EPSILON) / increment) * increment;
    return {
      amount: Number(roundedAmount.toFixed(fractionDigits)),
      fractionDigits,
    };
  }

  function formatAmount(sarCents, currency) {
    const displayAmount = resolveDisplayAmount(sarCents, currency);
    if (!displayAmount) return null;

    if (gccCurrencies.has(currency)) {
      const maximumFractionDigits = displayAmount.fractionDigits ?? 2;
      const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits,
      }).format(displayAmount.amount);
      const isArabic = String(config.locale || '').toLowerCase().startsWith('ar');
      const currencyLabel = isArabic ? gccArabicCurrencyLabels[currency] : currency;
      return `${formattedNumber} ${currencyLabel}`;
    }

    const numberFormatOptions = {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
    };
    if (displayAmount.fractionDigits !== null) {
      numberFormatOptions.minimumFractionDigits = displayAmount.fractionDigits;
      numberFormatOptions.maximumFractionDigits = displayAmount.fractionDigits;
    }

    try {
      return new Intl.NumberFormat(config.locale, numberFormatOptions).format(displayAmount.amount);
    } catch {
      const fallbackDigits = displayAmount.fractionDigits ?? 2;
      return `${currency} ${displayAmount.amount.toFixed(fallbackDigits)}`;
    }
  }

  function renderAmount(element) {
    const sarCents = Number(element.dataset.sarCents);
    if (!Number.isFinite(sarCents) || sarCents < 0) {
      element.hidden = true;
      return;
    }

    const formatted = formatAmount(sarCents, state.currency);
    if (!formatted) {
      element.hidden = true;
      return;
    }

    const value = element.querySelector('[data-local-currency-value]');
    if (!value) return;

    const prefix = element.dataset.priceVaries === 'true' ? config.copy.from : '';
    const visibleText = `${prefix}${formatted}`;
    if (value.textContent !== visibleText) value.textContent = visibleText;
    element.setAttribute(
      'aria-label',
      `${config.copy.displayEquivalent}: ${visibleText}. ${config.copy.checkoutInSar}`
    );
    element.hidden = false;
  }

  function renderWithin(root = document) {
    if (!state.rates?.[state.currency]) return;

    document.documentElement.classList.add('local-currency-ready');

    const amountElements = root.matches?.('[data-local-currency-amount]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-local-currency-amount]') || []);
    amountElements.forEach(renderAmount);

    document.querySelectorAll('[data-local-currency-code]').forEach((element) => {
      element.textContent = state.currency;
    });

    document.querySelectorAll('[data-local-currency-active]').forEach((element) => {
      element.hidden = false;
    });

    document.querySelectorAll('[data-local-currency-summary]').forEach((element) => {
      const countryName = element.dataset.countryName || '';
      element.textContent = `${countryName} | ${state.currency}`;
    });

    document.querySelectorAll('[data-local-currency-select]').forEach((select) => {
      if (select.value !== state.currency) select.value = state.currency;
    });
  }

  function hideDisplay() {
    document.documentElement.classList.remove('local-currency-ready');
    document.querySelectorAll('[data-local-currency-amount], [data-local-currency-active]').forEach((element) => {
      element.hidden = true;
    });
  }

  function onCurrencyChange(event) {
    const select = event.target.closest('[data-local-currency-select]');
    if (!select) return;

    const nextCurrency = select.value;
    if (!supportedCurrencies.has(nextCurrency) || !state.rates?.[nextCurrency]) {
      select.value = state.currency;
      return;
    }

    state.currency = nextCurrency;
    safeStorageSet(PREFERENCE_KEY, nextCurrency);
    renderWithin(document);
  }

  function syncVariantAmounts(event) {
    const sectionId = String(event?.data?.sectionId || '');
    const variantPrice = Number(event?.data?.variant?.price);
    if (!sectionId || !Number.isFinite(variantPrice)) return;

    document
      .querySelectorAll(`[data-local-currency-variant-sync][data-local-currency-section="${CSS.escape(sectionId)}"]`)
      .forEach((element) => {
        element.dataset.sarCents = String(variantPrice);
        renderAmount(element);
      });
  }

  function observeDynamicContent() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) renderWithin(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  async function initialize() {
    document.addEventListener('change', onCurrencyChange);
    observeDynamicContent();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncVariantAmounts);
    }

    const cached = readCachedRates();
    if (cached) {
      state.rates = cached.rates;
      state.providerDate = cached.providerDate;
      renderWithin(document);
      if (cached.age <= FRESH_CACHE_MS) return;
    }

    try {
      const fresh = await requestRates();
      state.rates = fresh.rates;
      state.providerDate = fresh.providerDate;
      renderWithin(document);
    } catch {
      if (!cached) hideDisplay();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
